import React from 'react';
import {
	Switch as MuiSwitch,
	type SwitchProps as MuiSwitchProps,
} from '@mui/material';
import {
	useConfirmation,
	type ConfirmationOptions,
} from '@netx/core-components/Confirmation';
import {useConstCallback} from '@netx/core-components/hooks/useEvent';

export interface SwitchProps extends MuiSwitchProps {
	/**
	 * Confirmation options to show when the switch is toggled.
	 */
	confirm?: Omit<ConfirmationOptions, 'open'>;
}

export const SwitchPrimitive = (
	{confirm, checked, onChange, ...props}: SwitchProps,
	ref: any,
) => {
	const confirmation = useConfirmation();

	const handleChange = useConstCallback(
		(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
			// Sometimes a change event will come through that was not related to
			// interacting directly with the input
			// I cannot seem to recreate this in the tests yet, but this is an
			// important chack
			if (!event) return;

			if (!confirm) {
				onChange?.(event, checked);
				return;
			}

			// confirmation is async
			event.persist();
			confirmation(confirm).then(() => {
				onChange?.(event, checked);
			});
		},
	);

	return (
		<MuiSwitch ref={ref} {...props} checked={checked} onChange={handleChange} />
	);
};

export const Switch = React.forwardRef(SwitchPrimitive) as (
	props: SwitchProps,
) => JSX.Element;

export default Switch;
